import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, BookOpen, Heart, Brain, Leaf, HelpCircle, Sparkles, BookMarked } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';
import SEO from '../components/SEO';

const Resources = () => {
  const resourceCategories = [
    {
      id: 'anxiety-depression',
      title: 'Anxiety & Depression',
      icon: <Brain className="h-6 w-6" />,
      description: 'Learn about the signs, symptoms, and treatment options for anxiety and depression.',
      resources: [
        {
          title: 'Depression - NIMH',
          source: 'National Institute of Mental Health',
          url: 'https://www.nimh.nih.gov/health/topics/depression',
          description: 'Comprehensive information on depression including symptoms, causes, and treatment options.'
        },
        {
          title: 'Anxiety Disorders - NIMH',
          source: 'National Institute of Mental Health',
          url: 'https://www.nimh.nih.gov/health/topics/anxiety-disorders',
          description: 'Learn about different types of anxiety disorders and evidence-based treatments.'
        },
        {
          title: 'Understanding Anxiety and Depression',
          source: 'Anxiety & Depression Association of America',
          url: 'https://adaa.org',
          description: 'Resources, support groups, and information for those affected by anxiety and depression.'
        },
        {
          title: 'Mental Health Conditions',
          source: 'NAMI (National Alliance on Mental Illness)',
          url: 'https://www.nami.org/About-Mental-Illness/Mental-Health-Conditions',
          description: 'Educational resources and support for individuals and families affected by mental illness.'
        }
      ]
    },
    {
      id: 'grief-loss',
      title: 'Grief & Loss',
      icon: <Heart className="h-6 w-6" />,
      description: 'Resources to help navigate the journey through grief and loss.',
      resources: [
        {
          title: 'Grief: Coping with the Loss of a Loved One',
          source: 'American Psychological Association',
          url: 'https://www.apa.org/topics/families/grief',
          description: 'Understanding the grief process and healthy ways to cope with loss.'
        },
        {
          title: 'Grief Tools',
          source: 'American Psychological Association',
          url: 'https://www.apa.org/topics/grief/tools',
          description: 'Practical guidance and strategies for navigating grief and building resilience.'
        },
        {
          title: 'Understanding Grief',
          source: 'American Psychological Association',
          url: 'https://www.apa.org/topics/grief',
          description: 'Overview of grief including physiological and emotional responses to loss.'
        },
        {
          title: 'Prolonged Grief Disorder',
          source: 'American Psychiatric Association',
          url: 'https://www.psychiatry.org/patients-families/prolonged-grief-disorder',
          description: 'Information about prolonged grief disorder and when to seek professional help.'
        }
      ]
    },
    {
      id: 'stress-management',
      title: 'Stress Management',
      icon: <Leaf className="h-6 w-6" />,
      description: 'Techniques and strategies for managing stress in daily life.',
      resources: [
        {
          title: 'Relaxation Techniques for Stress',
          source: 'Mayo Clinic',
          url: 'https://www.mayoclinic.org/healthy-lifestyle/stress-management/in-depth/relaxation-technique/art-20045368',
          description: 'Learn about deep breathing, meditation, progressive muscle relaxation, and more.'
        },
        {
          title: 'Stress Management',
          source: 'Mayo Clinic',
          url: 'https://www.mayoclinic.org/healthy-lifestyle/stress-management/basics/stress-basics/hlv-20049495',
          description: 'Comprehensive guide to understanding and managing stress effectively.'
        },
        {
          title: '5 Tips to Manage Stress',
          source: 'Mayo Clinic Health System',
          url: 'https://www.mayoclinichealthsystem.org/hometown-health/speaking-of-health/5-tips-to-manage-stress',
          description: 'Practical tips including exercise, healthy eating, and relaxation techniques.'
        },
        {
          title: 'Stress Relievers',
          source: 'Mayo Clinic',
          url: 'https://www.mayoclinic.org/healthy-lifestyle/stress-management/in-depth/stress-relievers/art-20047257',
          description: 'Quick stress relief techniques you can practice anywhere.'
        }
      ]
    },
    {
      id: 'self-care',
      title: 'Self-Care',
      icon: <BookOpen className="h-6 w-6" />,
      description: 'Nurture your physical and mental well-being with self-care practices.',
      resources: [
        {
          title: 'Self-Care for Mental Health',
          source: 'SAMHSA',
          url: 'https://www.samhsa.gov/technical-assistance/dtac/ccp/self-care',
          description: 'Foundational components of resilience including sleep, nutrition, exercise, and relaxation.'
        },
        {
          title: 'Caring for Yourself',
          source: 'SAMHSA',
          url: 'https://www.samhsa.gov/find-support/helping-someone/caring-for-yourself',
          description: 'Tips for self-care when supporting someone with mental health challenges.'
        },
        {
          title: 'Creating a Healthier Life',
          source: 'SAMHSA',
          url: 'https://library.samhsa.gov/product/creating-healthier-life-step-step-guide-wellness/sma16-4958',
          description: 'A guide to the eight dimensions of wellness: social, emotional, physical, and more.'
        },
        {
          title: 'Mental Health and Self-Care',
          source: 'SAMHSA',
          url: 'https://www.samhsa.gov/mental-health',
          description: 'Overview of mental health resources and support services.'
        }
      ]
    },
    {
      id: 'teen-mental-health',
      title: 'Teen Mental Health',
      icon: <Sparkles className="h-6 w-6" />,
      description: 'Resources specifically for adolescents and their parents navigating mental health challenges.',
      resources: [
        {
          title: 'U.S. Teens Need Far More Emotional and Social Support',
          source: 'American Psychological Association',
          url: 'https://www.apa.org/monitor/2025/04-05/teen-social-emotional-support',
          description: 'Addressing loneliness and isolation is crucial to fighting the youth mental health crisis.'
        },
        {
          title: 'Child and Adolescent Mental Health',
          source: 'National Institute of Mental Health',
          url: 'https://www.nimh.nih.gov/health/topics/child-and-adolescent-mental-health',
          description: 'Comprehensive guide on mental health topics for children and teens including warning signs and treatment.'
        },
        {
          title: 'Kids, Teens and Young Adults',
          source: 'NAMI (National Alliance on Mental Illness)',
          url: 'https://www.nami.org/kids-teens-and-young-adults/',
          description: 'Resources and support specifically designed for young people experiencing mental health challenges.'
        },
        {
          title: 'Adolescent Mental Health Resources',
          source: 'Office of Population Affairs (HHS)',
          url: 'https://opa.hhs.gov/adolescent-health/mental-health-adolescents/mental-health-resources',
          description: 'Federal resources including screening tools and crisis helplines for teens and families.'
        },
        {
          title: 'Youth Mental Health',
          source: 'CDC Healthy Youth',
          url: 'https://www.cdc.gov/healthy-youth/mental-health/index.html',
          description: 'Tips for parents on building resilience and supporting teen mental health.'
        },
        {
          title: 'How to Help Children and Teens Manage Stress',
          source: 'American Psychological Association',
          url: 'https://www.apa.org/topics/children/stress',
          description: 'Practical strategies for parents to help young people cope with stress and anxiety.'
        }
      ]
    },
    {
      id: 'when-to-seek-help',
      title: 'When to Seek Therapy',
      icon: <HelpCircle className="h-6 w-6" />,
      description: 'Recognize the signs that it may be time to talk to a professional.',
      resources: [
        {
          title: 'Mental Health: Overcoming the Stigma',
          source: 'Mayo Clinic',
          url: 'https://www.mayoclinic.org/healthy-lifestyle/adult-health/in-depth/mental-health/art-20044098',
          description: 'When to seek help and how to overcome barriers to getting mental health support.'
        },
        {
          title: 'Do I Need Help?',
          source: 'National Institute of Mental Health',
          url: 'https://www.nimh.nih.gov/health/publications/my-mental-health-do-i-need-help',
          description: 'A guide to understanding when it\'s time to seek professional mental health support.'
        },
        {
          title: 'Get Professional Help If You Need It',
          source: 'Mental Health America',
          url: 'https://mhanational.org/resources/get-professional-help-if-you-need-it/',
          description: 'Understanding the benefits of professional mental health treatment.'
        },
        {
          title: 'Warning Signs and When to Ask for Help',
          source: 'The Jed Foundation',
          url: 'https://jedfoundation.org/resource/mental-health-warning-signs-and-when-to-ask-for-help/',
          description: 'Recognizing mental health warning signs and taking action.'
        }
      ]
    }
  ];

  const crisisResources = [
    {
      title: '988 Suicide & Crisis Lifeline',
      description: 'Call or text 988 for 24/7 support',
      url: 'https://988lifeline.org'
    },
    {
      title: 'Crisis Text Line',
      description: 'Text HOME to 741741',
      url: 'https://www.crisistextline.org'
    },
    {
      title: 'SAMHSA National Helpline',
      description: '1-800-662-4357 (24/7 treatment referrals)',
      url: 'https://www.samhsa.gov/find-help/national-helpline'
    }
  ];

  return (
    <>
      <SEO 
        title="Mental Health Resources - Helpful Articles & Information"
        description="Explore trusted mental health resources on anxiety, depression, grief, stress management, self-care, and when to seek therapy. Curated links from NIMH, APA, Mayo Clinic, and SAMHSA."
        keywords="mental health resources Lebanon PA, anxiety resources, depression help, grief support, stress management tips, self-care mental health, when to see therapist"
        url="/resources"
      />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-br from-teal-50 via-white to-slate-50">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 right-1/4 w-1/2 h-full bg-gradient-to-b from-teal-100/20 to-transparent" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <img 
                src={businessInfo.logo} 
                alt={businessInfo.name}
                className="h-32 w-auto mx-auto mb-6"
              />
              <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Resources</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mt-2 mb-6">
                Mental Health Resources
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Explore trusted resources from leading mental health organizations to support your journey toward wellness.
              </p>
            </div>
          </div>
        </section>

        {/* Crisis Resources Banner */}
        <section className="bg-slate-800 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-white mb-2">If You're in Crisis</h2>
              <p className="text-slate-300 text-sm">Help is available 24/7. You don't have to face this alone.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {crisisResources.map((resource, index) => (
                <a
                  key={index}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-slate-700 hover:bg-slate-600 rounded-lg p-4 text-center transition-colors"
                >
                  <h3 className="text-teal-400 font-semibold">{resource.title}</h3>
                  <p className="text-slate-300 text-sm">{resource.description}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Resource Categories */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16">
              {resourceCategories.map((category) => (
                <div key={category.id} id={category.id}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center text-teal-600">
                      {category.icon}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-800">{category.title}</h2>
                      <p className="text-slate-600">{category.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {category.resources.map((resource, index) => (
                      <a
                        key={index}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group"
                      >
                        <Card className="h-full border-0 shadow-md hover:shadow-lg transition-all duration-300 group-hover:border-teal-200">
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-slate-800 group-hover:text-teal-600 transition-colors">
                                {resource.title}
                              </h3>
                              <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-teal-500 flex-shrink-0 mt-1" />
                            </div>
                            <p className="text-sm text-teal-600 mb-2">{resource.source}</p>
                            <p className="text-slate-600 text-sm">{resource.description}</p>
                          </CardContent>
                        </Card>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recommended Books Section */}
        <section className="py-20 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center text-teal-600">
                <BookMarked className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Recommended Books</h2>
                <p className="text-slate-600">Helpful reading for your mental health journey</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "Codependent No More", author: "Melody Beattie", amazonUrl: "https://www.amazon.com/s?k=Codependent+No+More+Melody+Beattie" },
                { title: "Feeling Good", author: "David Burns", amazonUrl: "https://www.amazon.com/s?k=Feeling+Good+David+Burns" },
                { title: "Boundaries", author: "Henry Cloud & John Townsend", amazonUrl: "https://www.amazon.com/s?k=Boundaries+Henry+Cloud+John+Townsend" },
                { title: "It's Ok That You're Not Ok", author: "Megan Devine", amazonUrl: "https://www.amazon.com/s?k=Its+Ok+That+Youre+Not+Ok+Megan+Devine" },
                { title: "Adult Children of Emotionally Immature Parents", author: "Lindsay C. Gibson", amazonUrl: "https://www.amazon.com/s?k=Adult+Children+Emotionally+Immature+Parents+Lindsay+Gibson" },
                { title: "The Seven Principles for Making Marriage Work", author: "John M. Gottman", amazonUrl: "https://www.amazon.com/s?k=Seven+Principles+Making+Marriage+Work+John+Gottman" },
                { title: "I Hate You, Don't Leave Me: Understanding the Borderline Personality", author: "Kreisman & Straus", amazonUrl: "https://www.amazon.com/s?k=I+Hate+You+Dont+Leave+Me+Kreisman+Straus" },
                { title: "Dare", author: "Barry McDonagh", amazonUrl: "https://www.amazon.com/s?k=Dare+Barry+McDonagh" },
                { title: "After the Affair", author: "Janis A. Spring", amazonUrl: "https://www.amazon.com/s?k=After+the+Affair+Janis+Spring" },
                { title: "The Body Keeps the Score", author: "Bessel van der Kolk", amazonUrl: "https://www.amazon.com/s?k=The+Body+Keeps+the+Score+Bessel+van+der+Kolk" },
                { title: "We All Have Parts", author: "Colleen West", amazonUrl: "https://www.amazon.com/s?k=We+All+Have+Parts+Colleen+West" },
                { title: "It Didn't Start With You", author: "Mark Wolynn", amazonUrl: "https://www.amazon.com/s?k=It+Didnt+Start+With+You+Mark+Wolynn" },
                { title: "How We Love", author: "Milan & Kay Yerkovich", amazonUrl: "https://www.amazon.com/s?k=How+We+Love+Milan+Kay+Yerkovich" },
              ].map((book, index) => (
                <a
                  key={index}
                  href={book.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <Card className="h-full border-0 shadow-md bg-white hover:shadow-lg transition-all duration-300">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-500 flex-shrink-0 mt-0.5 group-hover:bg-teal-100 group-hover:text-teal-600 transition-colors">
                          <BookOpen className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-slate-800 leading-tight group-hover:text-teal-600 transition-colors">{book.title}</h3>
                            <ExternalLink className="h-3 w-3 text-slate-400 group-hover:text-teal-500 flex-shrink-0 mt-1" />
                          </div>
                          <p className="text-sm text-teal-600 mt-1">{book.author}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-0 shadow-md bg-white">
              <CardContent className="p-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Important Note</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  These resources are provided for educational purposes only and are not a substitute for professional mental health treatment. 
                  If you are experiencing a mental health crisis, please contact emergency services or one of the crisis resources listed above. 
                  For personalized support, we encourage you to reach out and schedule a consultation.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-teal-500 to-teal-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Take the Next Step?
            </h2>
            <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
              Reading about mental health is a great first step. When you're ready for personalized support, we're here to help.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white hover:bg-slate-100 text-teal-600 px-8 py-6 text-lg">
                Schedule a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Resources;
